import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { X, Sparkles, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * LinkSuggestionsBanner + Modal — surfaces the dynamic list of
 * unlinked recipe ingredients that could now be linked to real
 * catalogue products (typically ones that arrived via a recent
 * delivery). Nothing auto-links; the chef confirms each pair.
 */
export function LinkSuggestionsBanner({ restaurantId, onOpen }) {
    const { t } = useTranslation();
    const [count, setCount] = useState(0);

    const refresh = useCallback(async () => {
        if (!restaurantId) return;
        try {
            const { data } = await api.get("/recipe-link-suggestions",
                { params: { restaurant_id: restaurantId } });
            setCount((data?.suggestions || []).length);
        } catch (_e) { setCount(0); }
    }, [restaurantId]);

    useEffect(() => { refresh(); }, [refresh]);

    if (!count) return null;
    return (
        <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50
                         px-4 py-3 flex items-center justify-between gap-3"
              data-testid="link-suggestions-banner">
            <div className="flex items-center gap-2 min-w-0">
                <Sparkles className="h-4 w-4 text-amber-700 shrink-0" />
                <div className="text-sm text-amber-900">
                    <span className="font-semibold">
                        {t("linkSuggestions.canBeLinked", { defaultValue: "{{count}} ingredients can now be linked to products", count })}
                    </span>
                    <span className="ml-2 text-amber-800/80">
                        {t("linkSuggestions.confirmMatches", "Confirm the matches so costs and stock work automatically.")}
                    </span>
                </div>
            </div>
            <button data-testid="link-suggestions-review"
                onClick={() => onOpen(refresh)}
                className="shrink-0 inline-flex items-center gap-1 rounded-md
                            bg-amber-900 text-white text-xs font-semibold
                            px-3 py-1.5 hover:bg-amber-800">
                Review <ChevronRight className="h-3.5 w-3.5" />
            </button>
        </div>
    );
}


export function LinkSuggestionsModal({ open, restaurantId, onClose, onLinked }) {
    const { t } = useTranslation();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/recipe-link-suggestions",
                { params: { restaurant_id: restaurantId } });
            setRows(data?.suggestions || []);
        } finally { setLoading(false); }
    }, [restaurantId]);
    useEffect(() => { if (open && restaurantId) reload(); },
              [open, restaurantId, reload]);

    if (!open) return null;

    const link = async (row) => {
        // Fetch the recipe, replace the ingredient row's fields, PATCH back.
        try {
            const { data: recipe } = await api.get(`/recipes/${row.recipe_id}`);
            const updated = (recipe.ingredients || []).map((i) => (
                i.id === row.ingredient_row_id
                    ? {
                        ...i,
                        ingredient_name: row.product_name,
                        unit: row.product_unit || i.unit,
                        product_id: row.product_id,
                        match_confidence: row.confidence,
                        user_confirmed: true,
                    }
                    : i
            ));
            await api.patch(`/recipes/${row.recipe_id}`,
                { ingredients: updated });
            toast.success(t("linkSuggestions.linkedCostAndStockWill", "Linked. Cost and stock will update now."));
            setRows((prev) => prev.filter(
                (r) => !(r.recipe_id === row.recipe_id
                          && r.ingredient_row_id === row.ingredient_row_id)));
            onLinked?.();
        } catch (e) {
            toast.error(t("linkSuggestions.couldNotLinkTryAgain", "Could not link — try again."));
        }
    };

    const dismiss = async (row) => {
        try {
            await api.post(
                `/recipes/${row.recipe_id}/ingredients/`
                + `${row.ingredient_row_id}/dismiss-candidate`,
                { product_id: row.product_id });
            toast.info(t("linkSuggestions.ignoredWeWonTSuggest", "Ignored. We won't suggest this again."));
            setRows((prev) => prev.filter(
                (r) => !(r.recipe_id === row.recipe_id
                          && r.ingredient_row_id === row.ingredient_row_id
                          && r.product_id === row.product_id)));
        } catch (_e) { toast.error(t("linkSuggestions.couldNotSaveTryAgain", "Could not save — try again.")); }
    };

    return (
        <div className="fixed inset-0 z-50 grid place-items-center
                          bg-black/40 p-4"
              data-testid="link-suggestions-modal">
            <div className="w-full max-w-2xl rounded-xl bg-white shadow-2xl
                              max-h-[85vh] flex flex-col">
                <div className="flex items-start justify-between p-5 border-b">
                    <div>
                        <h2 className="text-lg font-semibold text-neutral-900">
                            {t("linkSuggestions.linkIngredientsToProducts", "Link ingredients to products")}
                        </h2>
                        <p className="text-sm text-neutral-500 mt-0.5">
                            {t("linkSuggestions.confirmMatchesBelow", "Confirm the matches below so costs and stock work automatically.")}
                        </p>
                    </div>
                    <button data-testid="link-suggestions-close"
                        onClick={onClose}
                        className="rounded-md p-1.5 hover:bg-neutral-100">
                        <X className="h-4 w-4 text-neutral-500" />
                    </button>
                </div>
                <div className="p-5 overflow-y-auto space-y-4">
                    {loading && (
                        <div className="text-sm text-neutral-500">
                            {t("linkSuggestions.lookingForMatches", "Looking for matches …")}
                        </div>
                    )}
                    {!loading && rows.length === 0 && (
                        <div className="text-sm text-neutral-500 text-center py-8">
                            {t("linkSuggestions.allIngredientsAreUpTo", "All ingredients are up to date.")}
                        </div>
                    )}
                    {rows.map((row) => (
                        <div key={`${row.recipe_id}-${row.ingredient_row_id}`}
                              data-testid={`link-suggestion-${row.ingredient_row_id}`}
                              className="rounded-lg border border-neutral-200 p-4">
                            <div className="text-[11px] font-semibold
                                              tracking-wide uppercase text-neutral-500">
                                {row.recipe_name}
                            </div>
                            <div className="mt-0.5 text-base font-semibold
                                              text-neutral-900">
                                {row.ingredient_name}
                            </div>
                            <div className="mt-3 rounded-md bg-neutral-50 p-3
                                              text-sm">
                                <div>
                                    <span className="text-neutral-500">{t("linkSuggestions.possibleMatch", "Possible match:")} </span>
                                    <span className="font-semibold text-neutral-900">
                                        {row.product_name}
                                    </span>
                                </div>
                                <div className="text-neutral-500 mt-0.5">
                                    Confidence:{" "}
                                    <span className="font-semibold text-emerald-700">
                                        {Math.round((row.confidence || 0) * 100)}%
                                    </span>
                                    {row.reason ? ` · ${row.reason}` : ""}
                                </div>
                            </div>
                            <div className="mt-3 flex items-center gap-2 flex-wrap">
                                <button data-testid={`link-suggestion-link-${row.ingredient_row_id}`}
                                    onClick={() => link(row)}
                                    className="h-8 rounded-md bg-emerald-700
                                                 text-white text-xs font-semibold
                                                 px-3 hover:bg-emerald-800">
                                    {t("linkSuggestions.linkProduct", "Link Product")}
                                </button>
                                <button data-testid={`link-suggestion-dismiss-${row.ingredient_row_id}`}
                                    onClick={() => dismiss(row)}
                                    className="h-8 rounded-md border border-neutral-300
                                                 text-neutral-700 text-xs font-semibold
                                                 px-3 hover:bg-neutral-50">
                                    {t("linkSuggestions.notThisOne", "Not this one")}
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
